/**
 * The things a pilot's hands touch — switches, breakers, knobs, the trim wheel, the harness.
 *
 * ## Why this is the last big lever
 *
 * The cabin, the occlusion, the surface maps and the environment map between them made the cockpit
 * look like a *lit, textured room*. What it still did not look like was a machine anyone operates.
 * A real cockpit is dense with small hard objects at hand height: a row of toggle switches under
 * the panel, a grid of circuit breakers, a trim wheel, a fuel selector, belts hanging over the
 * seat. None of it is decoration — it is the difference between a surface and a workplace, and it
 * is what the eye counts when deciding whether a picture is of somewhere real.
 *
 * ## Merged, like everything else here
 *
 * Around ninety primitives arrive as **three meshes**: dark plastic, bright metal, and the placard
 * atlas. Submitted individually they would be ninety draw calls out of a 350 budget that terrain
 * already takes nearly two hundred of, for geometry that never moves.
 *
 * The one thing that is *not* merged is anything that has to animate — the yoke, the levers and the
 * pedals stay where they are, in `cockpit.ts`, because a merged mesh has no parts to move.
 *
 * ## Frames
 *
 * Panel furniture is built in the **panel's** local frame and parented to it, so it inherits the
 * 12° tilt for free rather than each switch having to reproduce it. Cabin furniture — the pedestal,
 * the harness — is built in the structure frame. Getting that backwards puts a switch row hovering
 * a centimetre off a tilted panel, converging at one end, which is a mistake that reads as a
 * modelling accident rather than as a bug.
 *
 * `updateFixed` / `updateFrame`: neither. Built once; nothing here moves.
 */

import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  PlaneGeometry,
  TorusGeometry,
  type BufferGeometry,
  type Material,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { DEG2RAD } from '../core/math.js';
import { placardUv, type PlacardId } from './cockpitPlacards.js';
import type { CabinShape } from './cockpitShell.js';

/** The three surfaces the furniture is made of, plus the atlas. */
export interface DetailMaterials {
  /** Switch bodies, breaker collars, knobs — moulded black plastic. */
  plastic: Material;
  /** Toggle levers, breaker pins, the trim wheel's rim — bright metal. */
  metal: Material;
  /** Webbing. */
  webbing: Material;
  /** Unlit, textured with the placard atlas. */
  placard: Material;
}

export interface CockpitDetails {
  /** Parent to the panel's tilted frame. */
  panelGroup: Group;
  /** Parent to the cockpit structure. */
  cabinGroup: Group;
  geometries: BufferGeometry[];
}

/** Where the panel furniture goes, per aircraft. */
export interface DetailLayout {
  /** Half the panel's width [m] — furniture is laid out against its real edges. */
  panelHalfWidthM: number;
  /** A yoke means a light-aeroplane panel; a stick means consoles and armament. */
  control: 'yoke' | 'stick';
}

function box(w: number, h: number, d: number, x: number, y: number, z: number): BufferGeometry {
  const geometry = new BoxGeometry(w, h, d);
  geometry.translate(x, y, z);
  return geometry;
}

function cylinder(
  radius: number,
  height: number,
  x: number,
  y: number,
  z: number,
  rotateXRad = Math.PI / 2,
  segments = 8,
): BufferGeometry {
  const geometry = new CylinderGeometry(radius, radius, height, segments);
  if (rotateXRad !== 0) geometry.rotateX(rotateXRad);
  geometry.translate(x, y, z);
  return geometry;
}

/**
 * A placard quad, sized from its atlas cell's aspect so the lettering is never stretched.
 *
 * Proud of the surface by a hair: coplanar geometry z-fights, and a label that flickers against its
 * own panel is worse than no label. 1 mm is below the depth buffer's argument and above nothing.
 */
function placard(id: PlacardId, widthM: number, x: number, y: number, z: number): BufferGeometry {
  const uv = placardUv(id);
  const geometry = new PlaneGeometry(widthM, widthM / uv.aspect);
  const attribute = geometry.getAttribute('uv');
  for (let i = 0; i < attribute.count; i++) {
    // PlaneGeometry's uvs are the unit square, so this is a straight remap into the atlas cell.
    attribute.setXY(
      i,
      uv.u0 + attribute.getX(i) * (uv.u1 - uv.u0),
      uv.v0 + attribute.getY(i) * (uv.v1 - uv.v0),
    );
  }
  attribute.needsUpdate = true;
  geometry.translate(x, y, z);
  return geometry;
}

/**
 * One toggle switch: a moulded base and a lever leaning out of it.
 *
 * The lever is what makes it read — a flush switch is a dot, and a cockpit's switches are visible
 * across the cabin precisely because they stick out and catch light along their length. Half are
 * built up and half down, because a real panel is never uniformly set and a row of identical
 * switches reads as wallpaper.
 */
function toggle(x: number, y: number, up: boolean, plastic: BufferGeometry[], metal: BufferGeometry[]): void {
  plastic.push(box(0.016, 0.02, 0.006, x, y, 0.013));
  const lever = new CylinderGeometry(0.0022, 0.003, 0.017, 6);
  lever.rotateX((up ? 22 : -22) * DEG2RAD);
  lever.translate(x, y + (up ? 0.008 : -0.008), 0.021);
  metal.push(lever);
}

/** One circuit breaker: a collar with a pin in it. */
function breaker(x: number, y: number, plastic: BufferGeometry[], metal: BufferGeometry[]): void {
  plastic.push(cylinder(0.0075, 0.006, x, y, 0.013));
  metal.push(cylinder(0.0042, 0.009, x, y, 0.017));
}

export function buildCockpitDetails(
  shape: CabinShape,
  layout: DetailLayout,
  materials: DetailMaterials,
): CockpitDetails {
  const panelGroup = new Group();
  panelGroup.name = 'cockpit-panel-details';
  const cabinGroup = new Group();
  cabinGroup.name = 'cockpit-cabin-details';

  const plastic: BufferGeometry[] = [];
  const metal: BufferGeometry[] = [];
  const webbing: BufferGeometry[] = [];
  const placards: BufferGeometry[] = [];
  const cabinPlastic: BufferGeometry[] = [];
  const cabinMetal: BufferGeometry[] = [];

  const half = layout.panelHalfWidthM;
  const isCabin = layout.control === 'yoke';

  // --- switch row, under the six-pack ---------------------------------------------------------
  /*
   * Placed against the panel's own left edge rather than at a fixed x: the three panels are 0.78,
   * 0.86 and 0.98 m wide, and a row authored for one of them ends up in the middle of another.
   */
  const switches: PlacardId[] = isCabin
    ? ['master', 'alt', 'beacon', 'nav', 'strobe', 'land', 'pitot', 'fuelPump']
    : ['masterArm', 'gun', 'missile', 'radar', 'ecm', 'canopy'];
  const switchY = -0.075;
  const spacing = 0.042;
  const switchX0 = -half + 0.07;
  switches.forEach((id, index) => {
    const x = switchX0 + index * spacing;
    toggle(x, switchY, index % 3 !== 1, plastic, metal);
    placards.push(placard(id, 0.038, x, switchY + 0.021, 0.0125));
  });
  // A rail behind the row, which is what the switches are actually mounted through.
  plastic.push(box(spacing * switches.length + 0.02, 0.032, 0.004, switchX0 + ((switches.length - 1) * spacing) / 2, switchY, 0.011));

  // --- circuit breakers ------------------------------------------------------------------------
  const breakerColumns = isCabin ? 6 : 4;
  const breakerRows = 4;
  const breakerX0 = half - 0.055 - (breakerColumns - 1) * 0.021;
  const breakerY0 = -0.02;
  for (let row = 0; row < breakerRows; row++) {
    for (let column = 0; column < breakerColumns; column++) {
      breaker(breakerX0 + column * 0.021, breakerY0 - row * 0.021, plastic, metal);
    }
  }
  placards.push(
    placard('breakers', 0.088, breakerX0 + ((breakerColumns - 1) * 0.021) / 2, breakerY0 + 0.019, 0.0125),
  );

  // --- the warning placard ---------------------------------------------------------------------
  /*
   * One per aeroplane, and a different one: a light aircraft's most-photographed placard is the
   * cabin's no-smoking plate, and a fighter's is the yellow-and-black stripe on the ejection seat.
   * They go in different places for the same reason — one is for passengers, one is for whoever
   * opens the canopy.
   */
  placards.push(
    isCabin
      ? placard('noSmoking', 0.075, -half + 0.06, 0.135, 0.0125)
      : placard('jettison', 0.075, -half + 0.06, 0.115, 0.0125),
  );
  if (!isCabin) placards.push(placard('gearWarn', 0.09, half - 0.075, 0.13, 0.0125));

  // --- pedestal furniture, in the cabin's frame -------------------------------------------------
  if (isCabin) {
    /*
     * The trim wheel, which on a light aeroplane is a large exposed wheel on the pedestal side —
     * one of the few controls big enough to identify a cockpit from a photograph of half of it.
     */
    const wheel = new TorusGeometry(0.055, 0.011, 6, 20);
    wheel.rotateY(Math.PI / 2);
    wheel.translate(0.075, shape.floorM + 0.2, -0.16);
    cabinMetal.push(wheel);
    cabinPlastic.push(cylinder(0.014, 0.05, 0.075, shape.floorM + 0.2, -0.16, 0, 10));

    // The fuel selector: a flat lever on a round plate, on top of the pedestal.
    cabinPlastic.push(cylinder(0.036, 0.006, 0, shape.floorM + 0.265, -0.16, 0, 12));
    cabinMetal.push(box(0.012, 0.008, 0.052, 0, shape.floorM + 0.272, -0.185));
  } else {
    // A side-console throttle: the grip a fighter pilot's left hand never leaves.
    const grip = new CylinderGeometry(0.019, 0.023, 0.085, 10);
    grip.rotateX(70 * DEG2RAD);
    grip.translate(-(shape.halfWidthM - 0.09), shape.sillM - 0.005, -0.14);
    cabinPlastic.push(grip);
    cabinMetal.push(box(0.03, 0.008, 0.13, -(shape.halfWidthM - 0.09), shape.sillM - 0.055, -0.16));
  }

  // --- the harness ------------------------------------------------------------------------------
  /*
   * Two straps over the seat back and a lap belt across the cushion. Almost always out of frame —
   * and that is not an argument against them, because the moment the camera looks down or the
   * aeroplane rolls, an empty seat is the thing that says nobody is flying this.
   */
  const seatY = shape.floorM + 0.24;
  for (const side of [-1, 1]) {
    const strap = new BoxGeometry(0.05, 0.5, 0.008);
    strap.rotateX(-8 * DEG2RAD);
    strap.translate(side * 0.12, seatY + 0.3, 0.36);
    webbing.push(strap);
  }
  webbing.push(box(0.42, 0.045, 0.01, 0, seatY + 0.05, 0.02));

  // --- merge ------------------------------------------------------------------------------------
  const geometries: BufferGeometry[] = [];
  const emit = (parts: BufferGeometry[], material: Material, group: Group, name: string): void => {
    if (parts.length === 0) return;
    const merged = mergeGeometries(parts, false);
    for (const part of parts) part.dispose();
    if (merged === null) return;
    merged.computeBoundingSphere();
    geometries.push(merged);
    const mesh = new Mesh(merged, material);
    mesh.name = name;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  };

  emit(plastic, materials.plastic, panelGroup, 'panel-plastic');
  emit(metal, materials.metal, panelGroup, 'panel-metal');
  emit(placards, materials.placard, panelGroup, 'panel-placards');
  emit(cabinPlastic, materials.plastic, cabinGroup, 'cabin-plastic');
  emit(cabinMetal, materials.metal, cabinGroup, 'cabin-metal-details');
  emit(webbing, materials.webbing, cabinGroup, 'cabin-webbing');

  return { panelGroup, cabinGroup, geometries };
}
